import {
  cn,
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  type IconType,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import {
  WORKSPACE_COLORS,
  WORKSPACE_ICONS,
} from '@/modules/onboarding/constants';

interface IWorkspaceIconPickerProps {
  selectedIcon: IconType;
  selectedColor: string;
  onIconChange?: (icon: IconType) => void;
  onColorChange?: (color: string) => void;
  readOnly?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

const sizeMap = {
  xs: 'w-8 h-8',
  sm: 'w-10 h-10',
  md: 'w-14 h-14',
  lg: 'w-20 h-20',
};

export function WorkspaceIconPicker({
  selectedIcon,
  selectedColor,
  onIconChange,
  onColorChange,
  size = 'md',
  readOnly = false,
}: IWorkspaceIconPickerProps) {
  const { t } = useTranslation('workspace');
  const handleIconChange = (icon: IconType) => {
    if (!readOnly && onIconChange) {
      onIconChange(icon);
    }
  };

  const handleColorChange = (color: string) => {
    if (!readOnly && onColorChange) {
      onColorChange(color);
    }
  };

  const triggerContent = (
    <div className="relative group">
      <button
        type="button"
        className={cn(
          'flex items-center justify-center rounded-[2px] p-1 shrink-0',
          sizeMap[size],
          !readOnly && 'cursor-pointer',
          readOnly && 'cursor-default',
        )}
        style={{ backgroundColor: selectedColor }}
        disabled={readOnly}
        data-testid="workspace-icon-picker-trigger"
      >
        <Icon
          type={selectedIcon}
          strokeWidth={1.5} // TODO: strokeWidth doesn't match Figma width due to Icon scaling. Revisit after design team discusses.
          className={cn(
            'w-full h-full text-white transition-opacity',
            !readOnly && 'group-hover:opacity-20',
          )}
        />
      </button>

      {/* Hover overlay with edit icon - only show if not readOnly */}
      {!readOnly && (
        <div
          className={cn(
            'cursor-pointer absolute inset-0 bg-white/20 dark:bg-black/20',
            'flex items-center justify-center rounded-[2px]',
            'opacity-0 group-hover:opacity-100 transition-opacity',
          )}
        >
          <Icon type="PenLine" className="size-5 text-foreground" />
        </div>
      )}
    </div>
  );

  // If readOnly, just return the trigger content without Popover
  if (readOnly) {
    return <div className="relative">{triggerContent}</div>;
  }

  return (
    <Popover>
      <div className="relative">
        <PopoverTrigger asChild>{triggerContent}</PopoverTrigger>
      </div>

      <PopoverContent className="w-62 p-1 dark:bg-neutral-800" align="start">
        <div className="px-2 py-1.5">
          <Text level="small" className="font-semibold">
            {t('workspace.iconPicker.chooseIconColor')}
          </Text>
        </div>

        {/* Icon Grid */}
        <div className="flex flex-wrap gap-1 p-2">
          {WORKSPACE_ICONS.map(iconType => (
            <Button
              key={iconType}
              type="button"
              className={cn(
                'hover:scale-110 hover:dark:bg-neutral-950 transition-transform',
                'flex items-center justify-center h-6 w-6',
                selectedIcon === iconType &&
                  'bg-neutral-100 dark:bg-neutral-950',
              )}
              onClick={() => handleIconChange(iconType)}
              variant="ghost"
              data-testid={`workspace-icon-picker-icon-${iconType}`}
            >
              <Icon
                type={iconType}
                size="sm"
                className="text-neutral-600 dark:text-neutral-400"
              />
            </Button>
          ))}
        </div>

        {/* Color Palette */}
        <div className="flex gap-1.5 p-2">
          {WORKSPACE_COLORS.map(color => (
            <button
              key={color}
              type="button"
              className={cn(
                'w-4 h-4 rounded-full border-2 hover:scale-110 cursor-pointer',
                'transition-transform flex items-center justify-center',
                selectedColor === color
                  ? 'dark:border-white shadow-lg border-gray-700'
                  : 'border-transparent',
              )}
              style={{ backgroundColor: color }}
              onClick={() => handleColorChange(color)}
            >
              {selectedColor === color && (
                <Icon type="Check" size={12} className="text-white" />
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
